/**
 * Custom patterns for Redact PDF, matched by an engine of our own instead of the browser's RegExp.
 *
 * Why: a browser regex can take minutes on a crafted or careless pattern such as (a+)+$ ("ReDoS"), and a page
 * cannot interrupt it. This engine runs every pattern as a Thompson NFA: it never backtracks, so the time grows
 * with (text length x pattern size), and on top of that every search has a hard budget of steps, a cap on the
 * length of one match, and caps on pattern length and size. A pattern that would go over any limit is refused with
 * a plain message before it runs, or stops with "too complex" instead of freezing the tab.
 *
 * Supported: literal characters, . (any character), \d \D \w \W \s \S, \b \B, escaped punctuation, classes like
 * [A-Z0-9-] and [^ ], groups ( ) and (?: ), alternation |, and the quantifiers ? * + {n} {n,} {n,m}.
 * Not supported (refused with a message): lookahead and lookbehind, back-references, named groups, ^ and $,
 * lazy and possessive quantifiers, \p{...}, and numbers above 100 in {n,m}.
 * The longest match at each position is used. Matches longer than MAX_MATCH_LENGTH characters are not found.
 */

export const MAX_PATTERN_LENGTH = 100;
export const MAX_SIMPLE_PATTERN_LENGTH = 60;
export const MAX_PATTERNS = 5;
export const MAX_MATCH_LENGTH = 200;
export const MAX_REPEAT = 100;
export const MAX_STATES = 3000;
/** Thread-steps one search may spend on one page of text before it stops with "too complex". */
export const STEP_BUDGET = 30_000_000;

export class PatternError extends Error {
  constructor(message: string) { super(message); this.name = "PatternError"; }
}
export class PatternBudgetError extends PatternError {
  constructor() { super("This pattern is too complex to run on this page. Make it more specific (fewer wildcards and repeats) and try again."); this.name = "PatternBudgetError"; }
}

type Test = (c: number) => boolean;
type Node =
  | { t: "set"; test: Test }
  | { t: "cat"; items: Node[] }
  | { t: "alt"; items: Node[] }
  | { t: "rep"; node: Node; min: number; max: number } // max = Infinity for unbounded
  | { t: "assert"; word: boolean };

const isDigit: Test = (c) => c >= 48 && c <= 57;
const isWord: Test = (c) => isDigit(c) || (c >= 65 && c <= 90) || (c >= 97 && c <= 122) || c === 95;
const isSpace: Test = (c) => c === 32 || (c >= 9 && c <= 13) || c === 0xa0 || c === 0xfeff || (c >= 0x2000 && c <= 0x200a) || c === 0x2028 || c === 0x2029 || c === 0x202f || c === 0x205f || c === 0x3000 || c === 0x1680;
const not = (t: Test): Test => (c) => !t(c);

const UNSUPPORTED_ESCAPE = /[pPkuxcB0-9]/;

class Parser {
  private p = 0;
  private cs: string[];
  constructor(src: string) { this.cs = Array.from(src); }

  parse(): Node {
    const n = this.alt();
    if (this.p < this.cs.length) throw new PatternError("There is a closing bracket ) with no opening bracket before it.");
    return n;
  }

  private alt(): Node {
    const items = [this.cat()];
    while (this.cs[this.p] === "|") { this.p++; items.push(this.cat()); }
    return items.length === 1 ? items[0] : { t: "alt", items };
  }

  private cat(): Node {
    const items: Node[] = [];
    while (this.p < this.cs.length && this.cs[this.p] !== "|" && this.cs[this.p] !== ")") items.push(this.quantified());
    return { t: "cat", items };
  }

  private quantified(): Node {
    const atom = this.atom();
    const q = this.cs[this.p];
    let min = -1, max = -1;
    if (q === "*") { min = 0; max = Infinity; this.p++; }
    else if (q === "+") { min = 1; max = Infinity; this.p++; }
    else if (q === "?") { min = 0; max = 1; this.p++; }
    else if (q === "{") {
      const m = /^\{(\d+)(?:(,)(\d*))?\}/.exec(this.cs.slice(this.p, this.p + 16).join(""));
      if (m) {
        min = Number(m[1]);
        max = m[2] ? (m[3] === "" ? Infinity : Number(m[3])) : min;
        if (min > MAX_REPEAT || (max !== Infinity && max > MAX_REPEAT)) throw new PatternError(`A repeat count can be at most ${MAX_REPEAT}.`);
        if (max < min) throw new PatternError("In a repeat like {2,5} the first number must not be larger than the second.");
        this.p += m[0].length;
      }
    }
    if (min < 0) return atom;
    if (atom.t === "assert") throw new PatternError("A word edge (\\b) can't be repeated.");
    const after = this.cs[this.p];
    if (after === "?" || after === "+") throw new PatternError("Lazy and possessive repeats aren't supported. The longest match is always used, so remove the extra ? or +.");
    return { t: "rep", node: atom, min, max };
  }

  private atom(): Node {
    const c = this.cs[this.p++];
    switch (c) {
      case "(": {
        if (this.cs[this.p] === "?") {
          if (this.cs[this.p + 1] === ":") this.p += 2;
          else throw new PatternError("Lookahead, lookbehind and named groups aren't supported. Use a plain group ( ) or (?: ).");
        }
        const inner = this.alt();
        if (this.cs[this.p] !== ")") throw new PatternError("A bracket ( is never closed.");
        this.p++;
        return inner;
      }
      case "[": return this.charClass();
      case ".": return { t: "set", test: (ch) => ch !== 10 && ch !== 13 && ch !== 0x2028 && ch !== 0x2029 };
      case "^": case "$": throw new PatternError("^ and $ aren't supported: the pattern is looked for anywhere in the page text. Use \\b for a word edge.");
      case "*": case "+": case "?": throw new PatternError(`"${c}" has nothing before it to repeat.`);
      case "\\": return this.escape(false) as Node;
      default: return literal(c);
    }
  }

  /** An escape after a backslash. Returns a node, or a test when inside a [class]. */
  private escape(inClass: boolean): Node | Test {
    const c = this.cs[this.p++];
    if (c === undefined) throw new PatternError("The pattern ends with a lone backslash.");
    const asNode = (test: Test): Node | Test => (inClass ? test : { t: "set", test });
    switch (c) {
      case "d": return asNode(isDigit);
      case "D": return asNode(not(isDigit));
      case "w": return asNode(isWord);
      case "W": return asNode(not(isWord));
      case "s": return asNode(isSpace);
      case "S": return asNode(not(isSpace));
      case "n": return asNode((ch) => ch === 10);
      case "t": return asNode((ch) => ch === 9);
      case "b": case "B":
        if (inClass) throw new PatternError("\\b isn't allowed inside [ ].");
        return { t: "assert", word: c === "b" };
    }
    if (/[0-9]/.test(c)) throw new PatternError("Back-references like \\1 aren't supported.");
    if (UNSUPPORTED_ESCAPE.test(c)) throw new PatternError(`\\${c} isn't supported. Use \\d, \\w, \\s, a [class] or the plain character.`);
    return inClass ? ((ch: number) => ch === c.codePointAt(0)) : literal(c);
  }

  private charClass(): Node {
    let negate = false;
    if (this.cs[this.p] === "^") { negate = true; this.p++; }
    const tests: Test[] = [];
    let first = true;
    for (;;) {
      const c = this.cs[this.p++];
      if (c === undefined) throw new PatternError("A [ is never closed with ].");
      if (c === "]" && !first) break;
      first = false;
      let lo: number | null = null;
      if (c === "\\") {
        const e = this.escape(true) as Test;
        const cp = this.cs[this.p - 1];
        // a single escaped character may start a range; a class escape (\d) may not
        if (/[dDwWsSnt]/.test(cp) && this.cs[this.p - 2] === "\\") { tests.push(e); continue; }
        lo = cp.codePointAt(0)!;
      } else lo = c.codePointAt(0)!;
      if (this.cs[this.p] === "-" && this.cs[this.p + 1] !== undefined && this.cs[this.p + 1] !== "]") {
        this.p++;
        let hiC = this.cs[this.p++];
        if (hiC === "\\") { this.escape(true); hiC = this.cs[this.p - 1]; if (/[dDwWsS]/.test(hiC)) throw new PatternError("A range like a-\\d isn't valid."); }
        const hi = hiC.codePointAt(0)!;
        if (hi < lo) throw new PatternError("A range in [ ] runs backwards (for example z-a).");
        const l = lo;
        tests.push((ch) => ch >= l && ch <= hi);
      } else {
        const l = lo;
        tests.push((ch) => ch === l);
      }
    }
    const any: Test = (ch) => tests.some((t) => t(ch));
    return { t: "set", test: negate ? not(any) : any };
  }
}

function literal(c: string): Node {
  const cp = c.codePointAt(0)!;
  return { t: "set", test: (ch) => ch === cp };
}

type Inst =
  | { op: "char"; test: Test }
  | { op: "split"; a: number; b: number }
  | { op: "jmp"; to: number }
  | { op: "assert"; word: boolean }
  | { op: "match" };

export interface SafeMatcher {
  readonly safe: true;
  /** Non-overlapping [start,end) ranges, the longest match at each position. Throws PatternBudgetError when over budget. */
  findAll(text: string): [number, number][];
}

function compile(root: Node, ignoreCase: boolean): Inst[] {
  const prog: Inst[] = [];
  const emit = (i: Inst) => {
    if (prog.length >= MAX_STATES) throw new PatternError("This pattern is too large. Use fewer or smaller repeats.");
    prog.push(i);
    return prog.length - 1;
  };
  const caseTest = (t: Test): Test => !ignoreCase ? t : (c) => {
    if (t(c)) return true;
    if (c < 0x10000) { const s = String.fromCharCode(c); const lo = s.toLowerCase().charCodeAt(0), up = s.toUpperCase().charCodeAt(0); return t(lo) || t(up); }
    return false;
  };
  const gen = (n: Node): void => {
    switch (n.t) {
      case "set": emit({ op: "char", test: caseTest(n.test) }); return;
      case "cat": for (const it of n.items) gen(it); return;
      case "assert": emit({ op: "assert", word: n.word }); return;
      case "alt": {
        const jumps: number[] = [];
        n.items.forEach((it, i) => {
          if (i < n.items.length - 1) {
            const sp = emit({ op: "split", a: 0, b: 0 });
            (prog[sp] as { a: number }).a = prog.length;
            gen(it);
            jumps.push(emit({ op: "jmp", to: 0 }));
            (prog[sp] as { b: number }).b = prog.length;
          } else gen(it);
        });
        for (const j of jumps) (prog[j] as { to: number }).to = prog.length;
        return;
      }
      case "rep": {
        for (let i = 0; i < n.min; i++) gen(n.node);
        if (n.max === Infinity) {
          const sp = emit({ op: "split", a: 0, b: 0 });
          (prog[sp] as { a: number }).a = prog.length;
          gen(n.node);
          emit({ op: "jmp", to: sp });
          (prog[sp] as { b: number }).b = prog.length;
        } else {
          const splits: number[] = [];
          for (let i = n.min; i < n.max; i++) {
            const sp = emit({ op: "split", a: 0, b: 0 });
            (prog[sp] as { a: number }).a = prog.length;
            splits.push(sp);
            gen(n.node);
          }
          for (const sp of splits) (prog[sp] as { b: number }).b = prog.length;
        }
        return;
      }
    }
  };
  gen(root);
  emit({ op: "match" });
  return prog;
}

function run(prog: Inst[], text: string): [number, number][] {
  const n = text.length;
  const out: [number, number][] = [];
  const mark = new Int32Array(prog.length).fill(-1);
  let gen = 0;
  let steps = 0;
  const wordAt = (i: number) => i >= 0 && i < n && isWord(text.charCodeAt(i));

  /** Adds the instruction at `pc` and everything reachable without reading a character, as seen at position `pos`. */
  const addThread = (list: number[], startPc: number, pos: number) => {
    const stack = [startPc];
    while (stack.length) {
      const pc = stack.pop()!;
      if (mark[pc] === gen) continue;
      mark[pc] = gen;
      steps++;
      const ins = prog[pc];
      if (ins.op === "jmp") stack.push(ins.to);
      else if (ins.op === "split") { stack.push(ins.b); stack.push(ins.a); }
      else if (ins.op === "assert") { if ((wordAt(pos - 1) !== wordAt(pos)) === ins.word) stack.push(pc + 1); }
      else list.push(pc);
    }
  };

  let s = 0;
  while (s < n) {
    let clist: number[] = [];
    gen++;
    addThread(clist, 0, s);
    let last = -1;
    const limit = Math.min(n, s + MAX_MATCH_LENGTH);
    for (let pos = s; ; pos++) {
      if (clist.some((pc) => prog[pc].op === "match")) last = pos;
      if (pos >= limit || clist.length === 0) break;
      const c = text.charCodeAt(pos);
      const next: number[] = [];
      gen++;
      for (const pc of clist) {
        steps++;
        const ins = prog[pc];
        if (ins.op === "char" && ins.test(c)) addThread(next, pc + 1, pos + 1);
      }
      if (steps > STEP_BUDGET) throw new PatternBudgetError();
      clist = next;
    }
    if (steps > STEP_BUDGET) throw new PatternBudgetError();
    if (last > s) { out.push([s, last]); s = last; } else s++;
  }
  return out;
}

/** Checks and compiles a pattern written as a regular expression. Throws PatternError with a plain message. */
export function compileSafePattern(source: string, opts: { ignoreCase?: boolean } = {}): SafeMatcher {
  const src = source.trim();
  if (!src) throw new PatternError("Type a pattern first.");
  if (Array.from(src).length > MAX_PATTERN_LENGTH) throw new PatternError(`A pattern can be at most ${MAX_PATTERN_LENGTH} characters long.`);
  const prog = compile(new Parser(src).parse(), !!opts.ignoreCase);
  return { safe: true, findAll: (text: string) => run(prog, text) };
}

/** A simple wildcard pattern as a regular-expression source: # is a digit, @ a letter, ? any one character, * any run of characters (no spaces), a space any run of spaces; everything else is literal. */
export function wildcardToSource(wild: string): string {
  const out: string[] = [];
  let lastSpace = false;
  for (const c of Array.from(wild.trim())) {
    const isSpaceChar = /\s/.test(c);
    if (isSpaceChar) { if (!lastSpace) out.push("\\s+"); lastSpace = true; continue; }
    lastSpace = false;
    if (c === "#") out.push("\\d");
    else if (c === "@") out.push("[A-Za-z]");
    else if (c === "?") out.push("\\S");
    else if (c === "*") out.push("\\S*");
    else out.push(/[A-Za-z0-9]/.test(c) ? c : "\\" + c);
  }
  return out.join("");
}

export type PatternMode = "wildcard" | "regex";
export interface CustomPattern { mode: PatternMode; source: string }

/** Checks one custom pattern. Returns a plain message when it can't be used, otherwise null. */
export function customPatternProblem(p: CustomPattern, opts: { ignoreCase?: boolean } = {}): string | null {
  const src = p.source.trim();
  if (!src) return null; // an empty line is simply skipped
  if (p.mode === "wildcard" && Array.from(src).length > MAX_SIMPLE_PATTERN_LENGTH) return `A simple pattern can be at most ${MAX_SIMPLE_PATTERN_LENGTH} characters long.`;
  try {
    compileSafePattern(p.mode === "wildcard" ? wildcardToSource(src) : src, opts);
    return null;
  } catch (e) {
    return e instanceof PatternError ? e.message : "This pattern can't be used.";
  }
}

/** The patterns typed in the box, one per line. Empty lines are skipped. */
export function parseCustomPatterns(text: string, mode: PatternMode): CustomPattern[] {
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((source) => ({ mode, source }));
}

/** One message for the whole box: the first problem, or "at most N patterns"; null when every line is usable. */
export function customPatternsProblem(text: string, mode: PatternMode, opts: { ignoreCase?: boolean } = {}): string | null {
  const list = parseCustomPatterns(text, mode);
  if (list.length > MAX_PATTERNS) return `Use at most ${MAX_PATTERNS} patterns (one per line).`;
  for (let i = 0; i < list.length; i++) {
    const m = customPatternProblem(list[i], opts);
    if (m) return list.length > 1 ? `Pattern ${i + 1}: ${m}` : m;
  }
  return null;
}

/** Compiles the box's patterns (already checked with customPatternsProblem). */
export function compileCustomPatterns(patterns: CustomPattern[], opts: { ignoreCase?: boolean } = {}): SafeMatcher[] {
  return patterns.map((p) => compileSafePattern(p.mode === "wildcard" ? wildcardToSource(p.source) : p.source, opts));
}
